import { configureStore } from "@reduxjs/toolkit";
import type { Middleware } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import rootReducer from './rootReducer';
import { offersApi } from '../Features/Offers';
import { hotelsApi } from '../Features/Hotels';
import { countriesApi } from '../Features/Countries';
import { categoriesApi } from '../Features/Categories';
import { bookingsApi } from '../Features/Bookings';
import { discountsApi } from '../Features/Discounts';
import { regionsApi } from '../Features/Regions';
import { tagsApi } from '../Features/Tags';
import { offerOptionsApi } from '../Features/OfferOptions';
import { companiesApi } from '../Features/Companies';
import { bankAccountsApi } from '../Features/BankAccounts';
import { reservationsApi } from '../Features/Reservation';
import { applicantsApi } from '../Features/Applicants';
import { offerProgramApi } from '../Features/OfferProgram';
import { programTemplatesApi } from '../Features/ProgramTemplates';
import { offerOptionsMappingsApi } from '../Features/OfferOptionsMappings';
import { offerBoardingPlacesApi } from '../Features/OfferBoardingPlaces';
import { contractTemplatesApi } from '../Features/ContractTemplates';
import { contractsApi } from '../Features/Contracts';
import { invoicesApi } from '../Features/Invoices';
import { invoiceImportApi } from '../Features/InvoiceImport/Api/invoiceImportApi';
import { expenseInvoicesApi } from '../Features/ExpenseInvoices/Service/expenseInvoicesApi';
import { nbpApi } from '../Features/ExternalRates/Service/nbpApi';
import { cashDocumentsApi } from '../Features/CashDocuments/Service/cashDocumentsApi';
import { authApi } from '../Features/Auth/api/authApi';
import { usersApi } from '../Features/UserManagement/Service/usersApi';
import { isPasswordChangeRequiredError } from '../Features/Auth/api/apiErrorHandler';
import { boardingPlaceTemplatesApi } from '../Features/BoardingPlaceTemplates';
import { ksefOutgoingApi } from '../Features/KsefOutgoing';
import { ksefIncomingApi } from '../Features/KsefIncoming';
import { bankDocumentsApi } from '../Features/BankDocuments';
import { accountingExportsApi } from '../Features/AccountingExports';
import { partiesApi } from '../Features/Parties';
import { reportsApi } from '../Features/Reports';
import { approvalTargetedRefreshMiddleware } from './CrossFeature/approvalTargetedRefreshMiddleware';
import { bankDocumentsReservationRefreshMiddleware } from './CrossFeature/bankDocumentsReservationRefreshMiddleware';
import { cashDocumentsReservationRefreshMiddleware } from './CrossFeature/cashDocumentsReservationRefreshMiddleware';
import { crossFeatureCacheBridgeMiddleware } from './CrossFeature/crossFeatureCacheBridgeMiddleware';
import { reservationCancellationCacheListener } from './CrossFeature/reservationCancellationCacheListener';
import { bookingReportsCacheListener } from './CrossFeature/bookingReportsCacheListener';

// RTK Query logger middleware for debugging API calls
const rtkQueryLogger: Middleware = () => (next) => (action: any) => {
  const reducerPath = typeof action.type === 'string' ? action.type.split('/')[0] : undefined;
  const endpointName = action.meta?.arg?.endpointName;
  const requestUrl = action.meta?.baseQueryMeta?.request?.url;
  const requestMethod = action.meta?.baseQueryMeta?.request?.method;
  const responseStatus = action.meta?.baseQueryMeta?.response?.status;
  const originalArgs = action.meta?.arg?.originalArgs;

  if (action.type?.endsWith('/pending')) {
    console.log('🚀 [RTK Query] Request pending:', {
      reducerPath,
      endpoint: endpointName,
      type: action.meta?.arg?.type,
      originalArgs,
      request: requestUrl ? { method: requestMethod, url: requestUrl } : undefined,
    });
  }

  if (action.type?.endsWith('/fulfilled')) {
    console.log('✅ [RTK Query] Response SUCCESS:', {
      reducerPath,
      endpoint: endpointName,
      type: action.meta?.arg?.type,
      originalArgs,
      status: responseStatus,
      request: requestUrl ? { method: requestMethod, url: requestUrl } : undefined,
      data: action.payload,
    });
  }

  if (action.type?.endsWith('/rejected')) {
    console.error('❌ [RTK Query] Response ERROR:', {
      reducerPath,
      endpoint: endpointName,
      type: action.meta?.arg?.type,
      originalArgs,
      status: responseStatus,
      request: requestUrl ? { method: requestMethod, url: requestUrl } : undefined,
      error: action.error,
      errorData: action.payload,
    });
  }

  return next(action);
};

const shouldLogRtkQuery = process.env.REACT_APP_ENABLE_RTK_QUERY_LOGGER === 'true';

/**
 * Password Change Required Middleware
 *
 * Intercepts API errors and redirects to /change-password
 * when backend returns 403 with errorCode: PASSWORD_CHANGE_REQUIRED
 */
const passwordChangeRequiredMiddleware: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const error = action.payload;

    if (isPasswordChangeRequiredError(error)) {
      console.warn('⚠️ [Auth] PASSWORD_CHANGE_REQUIRED detected - redirecting to /change-password');
      // Redirect to change password page
      window.location.href = '/change-password';
    }
  }

  return next(action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore Blob objects in RTK Query cache (PDF, Excel, etc.)
        ignoredActions: [
          'invoicesApi/executeQuery/fulfilled',
          'reservationsApi/executeQuery/fulfilled',
          'cashDocumentsApi/executeQuery/fulfilled',
          'invoiceImportApi/executeQuery/fulfilled',
          'invoiceImportApi/executeMutation/fulfilled',
        ],
        ignoredPaths: [
          'invoicesApi.queries',
          'reservationsApi.queries',
          'cashDocumentsApi.queries',
          'invoiceImportApi.queries',
          'invoiceImportApi.mutations',
        ],
        // Ignore Blob in action payloads and RTK Query meta objects (Request, Response)
        ignoredActionPaths: [
          'payload.data',
          'meta.baseQueryMeta.response',
          'meta.baseQueryMeta.request',
          'meta.arg.originalArgs.formData',
        ],
      },
    })
      .concat(shouldLogRtkQuery ? [rtkQueryLogger] : [])
      .concat(passwordChangeRequiredMiddleware) // Add PASSWORD_CHANGE_REQUIRED handler
      .concat(approvalTargetedRefreshMiddleware)
      .concat(bankDocumentsReservationRefreshMiddleware)
      .concat(cashDocumentsReservationRefreshMiddleware)
      .concat(crossFeatureCacheBridgeMiddleware)
      .concat(reservationCancellationCacheListener)
      .concat(bookingReportsCacheListener)
      .concat(
        authApi.middleware,
        offersApi.middleware,
        hotelsApi.middleware,
        countriesApi.middleware,
        categoriesApi.middleware,
        bookingsApi.middleware,
        discountsApi.middleware,
        regionsApi.middleware,
        tagsApi.middleware,
        offerOptionsApi.middleware,
        companiesApi.middleware,
        bankAccountsApi.middleware,
        reservationsApi.middleware,
        applicantsApi.middleware,
        offerProgramApi.middleware,
        programTemplatesApi.middleware,
        offerOptionsMappingsApi.middleware,
        offerBoardingPlacesApi.middleware,
        contractTemplatesApi.middleware,
        contractsApi.middleware,
        invoicesApi.middleware,
        invoiceImportApi.middleware,
        expenseInvoicesApi.middleware,
        nbpApi.middleware,
        cashDocumentsApi.middleware,
        boardingPlaceTemplatesApi.middleware,
        ksefOutgoingApi.middleware,
        ksefIncomingApi.middleware,
        bankDocumentsApi.middleware,
        accountingExportsApi.middleware,
        partiesApi.middleware,
        reportsApi.middleware,
        usersApi.middleware
      ),
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
